import type { CrudConfig } from '@startupquickstart/frontend';

// Define type based on our Prisma schema
export interface PromotionalOffer {
  id: string;
  organizationId: string;
  name?: string;
  title?: string;
  description?: string;
  status?: 'published' | 'draft';
  featuredImageId?: string;
  startDate?: string;
  endDate?: string;
  daysOfWeek?: (
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday'
  )[];
  startTime?: string;
  endTime?: string;
  termsAndConditions?: string;
  promotionalCode?: string;
  discountPercentage?: number;
  minUsers?: number;
  maxUsers?: number;
  maxUserRedemptions?: number;
  maxRedemptions?: number;
  currentRedemptions?: number;
  qrCodeId?: string;
  discountType?: string;
  discountValue?: number;
  validFrom?: string;
  validUntil?: string;
  isActive?: boolean;
  usageLimit?: number;
  usageCount?: number;
  createdAt: string;
  updatedAt: string;
  // Relations for display
  organization?: {
    id: string;
    name: string;
  };
  featuredImage?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
  };
}

/**
 * Promotional Offer CRUD configuration
 */
export const promotionalOfferCrudConfig: CrudConfig<PromotionalOffer> = {
  entityName: 'Promotional Offer',
  entityNamePlural: 'Promotional Offers',

  endpoints: {
    list: '/promotional-offers',
    single: (id: string) => `/promotional-offers/${id}`,
    create: '/promotional-offers',
    update: (id: string) => `/promotional-offers/${id}`,
    delete: (id: string) => `/promotional-offers/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: false,
      placeholder: 'Enter promotional offer name',
      validation: {
        max: 200,
      },
    },
    {
      key: 'title',
      label: 'Title',
      type: 'text',
      required: false,
      placeholder: 'Enter promotional offer title',
      validation: {
        max: 200,
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Enter description',
      validation: {
        max: 2000,
      },
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      required: false,
      defaultValue: 'published',
      options: [
        { label: 'Draft', value: 'draft' },
        { label: 'Published', value: 'published' },
      ],
    },
    {
      key: 'promotionalCode',
      label: 'Promotional Code',
      type: 'text',
      required: false,
      placeholder: 'Enter promotional code',
    },
    {
      key: 'discountType',
      label: 'Discount Type',
      type: 'select',
      required: false,
      options: [
        { label: 'Percentage', value: 'percentage' },
        { label: 'Fixed Amount', value: 'fixed_amount' },
      ],
    },
    {
      key: 'discountPercentage',
      label: 'Discount Percentage',
      type: 'number',
      required: false,
      placeholder: 'Enter discount percentage',
      validation: {
        min: 0,
        max: 100,
      },
    },
    {
      key: 'discountValue',
      label: 'Discount Value',
      type: 'number',
      required: false,
      placeholder: 'Enter discount value',
      validation: {
        min: 0,
      },
    },
    {
      key: 'startDate',
      label: 'Start Date',
      type: 'date',
      required: false,
    },
    {
      key: 'endDate',
      label: 'End Date',
      type: 'date',
      required: false,
    },
    {
      key: 'startTime',
      label: 'Start Time',
      type: 'text',
      required: false,
      placeholder: 'HH:MM',
    },
    {
      key: 'endTime',
      label: 'End Time',
      type: 'text',
      required: false,
      placeholder: 'HH:MM',
    },
    {
      key: 'validFrom',
      label: 'Valid From',
      type: 'datetime',
      required: false,
    },
    {
      key: 'validUntil',
      label: 'Valid Until',
      type: 'datetime',
      required: false,
    },
    {
      key: 'minUsers',
      label: 'Minimum Users',
      type: 'number',
      required: false,
      placeholder: 'Minimum users required',
      validation: {
        min: 0,
      },
    },
    {
      key: 'maxUsers',
      label: 'Maximum Users',
      type: 'number',
      required: false,
      placeholder: 'Maximum users allowed',
      validation: {
        min: 0,
      },
    },
    {
      key: 'maxUserRedemptions',
      label: 'Max User Redemptions',
      type: 'number',
      required: false,
      placeholder: 'Max redemptions per user',
      validation: {
        min: 0,
      },
    },
    {
      key: 'maxRedemptions',
      label: 'Max Redemptions',
      type: 'number',
      required: false,
      placeholder: 'Maximum total redemptions',
      validation: {
        min: 0,
      },
    },
    {
      key: 'usageLimit',
      label: 'Usage Limit',
      type: 'number',
      required: false,
      placeholder: 'Total usage limit',
      validation: {
        min: 0,
      },
    },
    {
      key: 'isActive',
      label: 'Is Active',
      type: 'boolean',
      required: false,
      defaultValue: true,
    },
    {
      key: 'termsAndConditions',
      label: 'Terms and Conditions',
      type: 'richtext',
      required: false,
      placeholder: 'Enter terms and conditions',
    },
    {
      key: 'featuredImageId',
      label: 'Featured Image',
      type: 'image',
      required: false,
      placeholder: 'Featured Image ID',
    },
    {
      key: 'currentRedemptions',
      label: 'Current Redemptions',
      type: 'number',
      readonly: true,
    },
    {
      key: 'usageCount',
      label: 'Usage Count',
      type: 'number',
      readonly: true,
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['name', 'title', 'promotionalCode', 'status', 'isActive', 'createdAt', 'updatedAt'],
    searchableFields: ['name', 'title', 'description', 'promotionalCode'],
    sortableFields: ['name', 'title', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'createdAt',
      direction: 'desc',
    },
    itemsPerPage: 10,
    filters: [
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { label: 'Published', value: 'published' },
          { label: 'Draft', value: 'draft' },
        ],
      },
      {
        key: 'isActive',
        label: 'Is Active',
        type: 'select',
        options: [
          { label: 'Active', value: 'true' },
          { label: 'Inactive', value: 'false' },
        ],
      },
    ],
    actions: {
      create: {
        enabled: true,
      },
      view: {
        enabled: true,
      },
      edit: {
        enabled: true,
      },
      delete: {
        enabled: true,
      },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Basic Information',
        description: 'Core promotional offer details',
        fields: ['name', 'title', 'status', 'featuredImageId', 'isActive', 'description'],
        defaultExpanded: true,
      },
      {
        title: 'Discount Information',
        description: 'Discount details and codes',
        fields: ['promotionalCode', 'discountType', 'discountPercentage', 'discountValue'],
        defaultExpanded: true,
      },
      {
        title: 'Validity Period',
        description: 'When this offer is valid',
        fields: ['startDate', 'endDate', 'startTime', 'endTime', 'validFrom', 'validUntil'],
        defaultExpanded: true,
      },
      {
        title: 'Usage Limits',
        description: 'Limits on who can use this offer',
        fields: ['minUsers', 'maxUsers', 'maxUserRedemptions', 'maxRedemptions', 'usageLimit'],
        defaultExpanded: true,
      },
      {
        title: 'Usage Statistics',
        description: 'Current usage statistics',
        fields: ['currentRedemptions', 'usageCount'],
        defaultExpanded: true,
      },
      {
        title: 'Terms and Conditions',
        description: 'Legal and usage terms',
        fields: ['termsAndConditions'],
        defaultExpanded: false,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: false,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'General',
        description: 'General Information about the Promotional Offer',
        fields: ['name', 'title', 'featuredImageId', 'description', 'status', 'isActive'],
        columns: 1,
      },
      {
        title: 'Discount',
        description: 'Discount Information',
        fields: ['promotionalCode', 'discountType', 'discountPercentage', 'discountValue'],
        columns: 2,
      },
      {
        title: 'Validity',
        description: 'Validity Period',
        fields: ['startDate', 'endDate', 'startTime', 'endTime', 'validFrom', 'validUntil'],
        columns: 2,
      },
      {
        title: 'Usage Limits',
        description: 'Usage Limits',
        fields: ['minUsers', 'maxUsers', 'maxUserRedemptions', 'maxRedemptions', 'usageLimit'],
        columns: 2,
      },
      {
        title: 'Terms',
        description: 'Terms and Conditions',
        fields: ['termsAndConditions'],
        columns: 1,
      },
    ],
    validation: {},
  },
};
